// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — Filter Context
//
// Direct behavior port of lib/filter-context.ts from the standalone repo
// (automationenablementopena-anedashboard). Logic, types, and defaults are
// unchanged — only the file location and the React import style changed
// (that repo used Next.js 'use client' files; this is a plain CRA context).
// Do not "improve" the date math here without checking it against the
// original — this is intentionally a faithful port, not a rewrite.
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState } from 'react';
import { format, subWeeks } from 'date-fns';

export type FilterPreset = '4w' | '8w' | '12w' | '26w' | '52w' | 'custom';
export type TimeFilterType = 'day' | 'week' | 'month';

export interface FilterState {
  timeFilterType: TimeFilterType;
  selectedPeriods: string[];
  preset: FilterPreset;
  /** ISO date string YYYY-MM-DD */
  dateFrom: string;
  /** ISO date string YYYY-MM-DD */
  dateTo: string;
  /** Human-readable label shown in the filter status bar */
  label: string;
}

export interface AutomationFilterContextValue {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
}

export interface TimeFilterRequestParams {
  timeFilterType: TimeFilterType;
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export const PRESET_CONFIG: Record<
  Exclude<FilterPreset, 'custom'>,
  { weeks: number; label: string }
> = {
  '4w': { weeks: 4, label: 'last 4 weeks' },
  '8w': { weeks: 8, label: 'last 8 weeks' },
  '12w': { weeks: 12, label: 'last 12 weeks' },
  '26w': { weeks: 26, label: 'last 26 weeks' },
  '52w': { weeks: 52, label: 'last 52 weeks' },
};

export function buildPresetFilter(preset: Exclude<FilterPreset, 'custom'>): FilterState {
  const today = new Date();
  const { weeks, label } = PRESET_CONFIG[preset];
  return {
    timeFilterType: 'day',
    selectedPeriods: [],
    preset,
    dateFrom: format(subWeeks(today, weeks), 'yyyy-MM-dd'),
    dateTo: format(today, 'yyyy-MM-dd'),
    label,
  };
}

// Default state (12 weeks) — same default as the standalone dashboard.
export const defaultAutomationFilters: FilterState = buildPresetFilter('12w');

export function buildTimeFilterRequestParams(filters: FilterState): TimeFilterRequestParams {
  if (filters.timeFilterType === 'day') {
    return {
      timeFilterType: 'day',
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    };
  }

  return {
    timeFilterType: filters.timeFilterType,
    selectedPeriods: filters.selectedPeriods,
  };
}

export function validateFilterSelection(filters: FilterState): string | null {
  if (filters.timeFilterType === 'day') {
    if (!filters.dateFrom || !filters.dateTo) {
      return 'Select both from and to dates.';
    }
    return null;
  }

  if (!filters.selectedPeriods || filters.selectedPeriods.length === 0) {
    return filters.timeFilterType === 'week'
      ? 'Select at least one week.'
      : 'Select at least one month.';
  }

  return null;
}

export const AutomationFilterContext = createContext<AutomationFilterContextValue>({
  filters: defaultAutomationFilters,
  setFilters: () => {},
});

export const useAutomationFilters = (): AutomationFilterContextValue => useContext(AutomationFilterContext);

/**
 * Provider — owns the filter state for the Automation Insights page tree.
 * Scoped locally to this page (not lifted into App.tsx) since no other part
 * of the platform needs it.
 */
export function AutomationFilterProvider({ children }: { children: React.ReactNode }) {
  const [filters, setFilters] = useState<FilterState>(defaultAutomationFilters);

  return (
    <AutomationFilterContext.Provider value={{ filters, setFilters }}>
      {children}
    </AutomationFilterContext.Provider>
  );
}
